import React, { Component } from "react";
import { hasBecomeTrue } from "~/hocs/crud";
import { messages } from "./../../i18n-messages";
import { InjectedIntlProps, injectIntl } from "@ai360/core/intl";
import { model, service } from "./../data";
import {
    getAgBytesErrorClassNames,
    handlePicklistChange,
    mapToPicklistValue,
    memoizeSelectableOptions,
    onNumberChange,
    onTextChange,
    prepareSelectableOptions,
} from "~/admin/utils";

import { Equipment, IAddEditPanelProps } from "../interfaces";
// constants
import * as picklistService from "~/core/picklist/picklist-names";
import * as unitService from "~/core/units/unit-names";
import { adminData, GUID, ID, NAME, PICKLIST_GUID, VALUE } from "~/admin/data";
// Components
import {
    Checkbox,
    NumericInput,
    Section,
    SelectInput,
    SubSection,
    TextArea,
    TextInput,
} from "~/core";
import PhotoList from "./../../components/photo-list";
import AliasList from "./../../components/alias-list";
//Style
import "./add-edit-panel.css";

export class AddEditPanel extends Component<IAddEditPanelProps & InjectedIntlProps, any> {
    equipment: Equipment;

    getEquipmentParentTypeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getEquipmentTypeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getManufacturerOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getMaxSpeedUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getFuelCapacityUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getContainerSizeUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getDriveTrainOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getTransmissionTypeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getFuelEfficiencyUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getHorsePowerUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getFuelTypeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getPhysicalStateOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getWeightUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getTowingCapacityUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getDimensionUnitOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: GUID, label: NAME, id: ID })
    );
    getClassSizeOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );

    constructor(props) {
        super(props);
        this.equipment = props.record || {};
        this.state = {
            nextId: null,
        };
    }

    componentDidMount() {
        this.setupEquipmentRecord();
        this.props.setBreadcrumbs([""]);
        const { needs } = this.props;
        needs([this.props.getNextId(), this.props.fetchPicklistData(), this.props.fetchUnitData()]);

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(this.props.recordGuid)]);
        }
    }

    componentDidUpdate(prevProps) {
        if (hasBecomeTrue("fetchData", prevProps, this.props)) {
            this.props.liftRecordData(this.equipment);
        }
        if (this.props.addEditPanel.mode === "ADD") {
            this.setupNextId(this.props);
        } else {
            if (this.props.record && this.props.record !== prevProps.record) {
                this.equipment = this.props.record;
            }
        }
    }

    setupNextId = (props) => {
        if (props.nextId && props.nextId !== this.state.nextId) {
            this.equipment[model.PROPS_EQUIPMENT_ID] = props.nextId;
            this.setState({
                nextId: props.nextId,
            });
        }
    };

    setupEquipmentRecord = () => {
        const { addEditPanel } = this.props;
        this.equipment = {};
        if (addEditPanel.mode === "ADD") {
            this.equipment = service.getDefaultRecord();
        }
    };

    onPicklistChange = ({ type, guid }, value?, callback?) => {
        this.equipment = handlePicklistChange(this.equipment, { type, guid, value }, callback);
    };

    onPhysicalStateChange = ({ guid }, value) => {
        if (guid && value) {
            this.equipment[guid] = value.guid;
        } else {
            console.error(`${guid} / ${value} value is null`);
        }
    };

    onTextChange = (formKey, value, callback?: () => void) => {
        this.equipment = onTextChange(this.equipment, { formKey: [formKey], value }, callback);
    };

    onNumberChange = (formKey, value, callback?: () => void) => {
        this.equipment = onNumberChange(this.equipment, { formKey: [formKey], value }, callback);
    };

    setHeaderText = () => {
        this.props.setBreadcrumbs([this.equipment[model.PROPS_EQUIPMENT_NAME]]);
    };

    onEquipmentParentChange = () => {
        const picklistKey = picklistService.getPickListCode(
            picklistService.PICKLIST_EQUIPMENT_TYPE_NAME
        );
        this.props.needs([
            this.props.fetchPicklistData({
                url: picklistService.URL_GET_PICKLIST_CHILD_VALUE_LIST,
                picklistKey,
                model: {
                    parentPicklistName: "Equipment Parent Type",
                    picklistName: "Equipment Type Name",
                    picklistParentGuid: this.equipment[model.PROPS_EQUIPMENT_PARENT_TYPE_GUID],
                },
                forcedRequest: true,
            }),
        ]);
    };

    renderEquipmentInfo = () => {
        const { apiErrors, userRole } = this.props;
        const { formatMessage } = this.props.intl;
        const { equipment } = this;
        const { nextId } = this.state;
        return (
            <div className="section-container">
                <Section testId="equipment-general-section">
                    <SubSection testId="equipment-equipment-id-subsection">
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={9}
                            suppressFormatting
                            containerClassNames={["form-input-id"]}
                            placeholderText={formatMessage(messages.equipmentId)}
                            value={nextId || equipment[model.PROPS_EQUIPMENT_ID]}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_EQUIPMENT_ID, value)
                            }
                            required
                            testId="equipment-equipment-id-input"
                        />
                        {!userRole[model.PROPS_ACTIVE_INACTIVE] ? null : (
                            <Checkbox
                                className="active-checkbox"
                                onChange={(e, value) =>
                                    this.onTextChange(model.PROPS_ACTIVE_YN, value)
                                }
                                value={equipment[model.PROPS_ACTIVE_YN]}
                                label={formatMessage(messages.active)}
                                testId="equipment-is-active-checkbox"
                            />
                        )}
                    </SubSection>
                    <SubSection testId="equipment-type-subsection">
                        <SelectInput
                            tabIndex={0}
                            containerClassNames={getAgBytesErrorClassNames(211, apiErrors)}
                            autoFocus
                            openOnFocus={false}
                            required
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getEquipmentParentTypeOptions(
                                this.props[picklistService.PICKLIST_EQUIPMENT_PARENT_TYPE]
                            )}
                            placeholderText={formatMessage(messages.equipmentParentType)}
                            value={mapToPicklistValue({
                                options: this.getEquipmentParentTypeOptions(
                                    this.props[picklistService.PICKLIST_EQUIPMENT_PARENT_TYPE]
                                ),
                                selectedGuid: equipment[model.PROPS_EQUIPMENT_PARENT_TYPE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_EQUIPMENT_PARENT_TYPE,
                                        guid: model.PROPS_EQUIPMENT_PARENT_TYPE_GUID,
                                    },
                                    value
                                );
                                this.onEquipmentParentChange();
                            }}
                            testId="equipment-parent-type-select"
                        />
                        <SelectInput
                            tabIndex={0}
                            required
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            containerClassNames={getAgBytesErrorClassNames(213, apiErrors)}
                            disabled={!equipment[model.PROPS_EQUIPMENT_PARENT_TYPE]}
                            options={this.getEquipmentTypeOptions(
                                this.props[picklistService.PICKLIST_EQUIPMENT_TYPE_NAME]
                            )}
                            placeholderText={formatMessage(messages.equipmentType)}
                            value={mapToPicklistValue({
                                options: this.getEquipmentTypeOptions(
                                    this.props[picklistService.PICKLIST_EQUIPMENT_TYPE_NAME]
                                ),
                                selectedGuid: equipment[model.PROPS_EQUIPMENT_TYPE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_EQUIPMENT_TYPE,
                                        guid: model.PROPS_EQUIPMENT_TYPE_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-type-select"
                        />
                    </SubSection>
                </Section>
                <span className="no-bar section-fence"></span>
                <Section className="equipment-section" testId="equipment-manufacturer-section">
                    <SubSection
                        className="form-section-row-2"
                        testId="equipment-manufacturer-and-model-subsection"
                    >
                        <SelectInput
                            tabIndex={0}
                            required
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            containerClassNames={getAgBytesErrorClassNames(343, apiErrors)}
                            options={this.getManufacturerOptions(
                                this.props[picklistService.PICKLIST_MANUFACTURER]
                            )}
                            placeholderText={formatMessage(messages.manufacturerName)}
                            value={mapToPicklistValue({
                                options: this.getManufacturerOptions(
                                    this.props[picklistService.PICKLIST_MANUFACTURER]
                                ),
                                selectedGuid: equipment[model.PROPS_MANUFACTURER_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_MANUFACTURER,
                                        guid: model.PROPS_MANUFACTURER_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-manufacturer-name-select"
                        />
                        <TextInput
                            tabIndex={0}
                            maxLength={50}
                            containerClassNames={[
                                this.equipment[model.PROPS_EQUIPMENT_NAME],
                                ...getAgBytesErrorClassNames(382, apiErrors),
                            ]}
                            placeholderText={formatMessage(messages.modelName)}
                            value={equipment[model.PROPS_EQUIPMENT_NAME]}
                            required
                            onChange={(value) =>
                                this.onTextChange(
                                    model.PROPS_EQUIPMENT_NAME,
                                    value,
                                    this.setHeaderText
                                )
                            }
                            testId="equipment-model-name-input"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    renderDetailInfo1 = () => {
        const { apiErrors } = this.props;
        const { formatMessage } = this.props.intl;
        const { equipment } = this;
        return (
            <div className="section-container section-column">
                <Section
                    headerText={formatMessage(messages.fuelPerformanceInfo)}
                    testId="equipment-fuel-performance-info-section"
                >
                    <SubSection testId="equipment-max-speed-subsection">
                        <NumericInput
                            tabIndex={0}
                            containerClassNames={getAgBytesErrorClassNames(401, apiErrors)}
                            placeholderText={formatMessage(messages.maxSpeed)}
                            value={equipment[model.PROPS_MAX_SPEED] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) => this.onNumberChange(model.PROPS_MAX_SPEED, value)}
                            testId="equipment-max-speed-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getMaxSpeedUnitOptions(
                                this.props[unitService.UNIT_SPEED]
                            )}
                            placeholderText={formatMessage(messages.maxSpeedUnit)}
                            value={mapToPicklistValue({
                                options: this.getMaxSpeedUnitOptions(
                                    this.props[unitService.UNIT_SPEED]
                                ),
                                selectedGuid: equipment[model.PROPS_MAX_SPEED_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_MAX_SPEED_UNIT,
                                        guid: model.PROPS_MAX_SPEED_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-max-speed-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-fuel-capacity-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.fuelCapacity)}
                            value={equipment[model.PROPS_FUEL_CAPACITY] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_FUEL_CAPACITY, value)
                            }
                            testId="equipment-fuel-capacity-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getFuelCapacityUnitOptions(
                                this.props[unitService.UNIT_VOLUME]
                            )}
                            placeholderText={formatMessage(messages.fuelCapacityUnit)}
                            value={mapToPicklistValue({
                                options: this.getFuelCapacityUnitOptions(
                                    this.props[unitService.UNIT_VOLUME]
                                ),
                                selectedGuid: equipment[model.PROPS_FUEL_CAPACITY_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_FUEL_CAPACITY_UNIT,
                                        guid: model.PROPS_FUEL_CAPACITY_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-fuel-capacity-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-drivetrain-and-transmission-subsection">
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getDriveTrainOptions(
                                this.props[picklistService.PICKLIST_DRIVE_TRAIN]
                            )}
                            placeholderText={formatMessage(messages.drivetrain)}
                            value={mapToPicklistValue({
                                options: this.getDriveTrainOptions(
                                    this.props[picklistService.PICKLIST_DRIVE_TRAIN]
                                ),
                                selectedGuid: equipment[model.PROPS_DRIVE_TRAIN_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_DRIVE_TRAIN,
                                        guid: model.PROPS_DRIVE_TRAIN_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-drivetrain-select"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getTransmissionTypeOptions(
                                this.props[picklistService.PICKLIST_TRANSMISSION_TYPE]
                            )}
                            placeholderText={formatMessage(messages.transmissionType)}
                            value={mapToPicklistValue({
                                options: this.getTransmissionTypeOptions(
                                    this.props[picklistService.PICKLIST_TRANSMISSION_TYPE]
                                ),
                                selectedGuid: equipment[model.PROPS_TRANSMISSION_TYPE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_TRANSMISSION_TYPE,
                                        guid: model.PROPS_TRANSMISSION_TYPE_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-transmission-type-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-fuel-efficiency-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.fuelEfficiency)}
                            value={equipment[model.PROPS_FUEL_EFFICIENCY] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_FUEL_EFFICIENCY, value)
                            }
                            testId="equipment-fuel-efficiency-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            containerClassNames={getAgBytesErrorClassNames(403, apiErrors)}
                            options={this.getFuelEfficiencyUnitOptions(
                                this.props[unitService.UNIT_FUEL_EFFICIENCY]
                            )}
                            placeholderText={formatMessage(messages.fuelEfficiencyUnit)}
                            value={mapToPicklistValue({
                                options: this.getFuelEfficiencyUnitOptions(
                                    this.props[unitService.UNIT_FUEL_EFFICIENCY]
                                ),
                                selectedGuid: equipment[model.PROPS_FUEL_EFFICIENCY_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_FUEL_EFFICIENCY_UNIT,
                                        guid: model.PROPS_FUEL_EFFICIENCY_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-fuel-efficiency-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-horse-power-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.horsePower)}
                            value={equipment[model.PROPS_HORSE_POWER] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_HORSE_POWER, value)
                            }
                            testId="equipment-horse-power-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getHorsePowerUnitOptions(
                                this.props[unitService.UNIT_POWER]
                            )}
                            containerClassNames={getAgBytesErrorClassNames(404, apiErrors)}
                            placeholderText={formatMessage(messages.horsePowerUnit)}
                            value={mapToPicklistValue({
                                options: this.getHorsePowerUnitOptions(
                                    this.props[unitService.UNIT_POWER]
                                ),
                                selectedGuid: equipment[model.PROPS_HORSE_POWER_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_HORSE_POWER_UNIT,
                                        guid: model.PROPS_HORSE_POWER_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-horse-power-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-fuel-type-and-cylinders-subsection">
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getFuelTypeOptions(
                                this.props[picklistService.PICKLIST_FUEL_TYPE]
                            )}
                            placeholderText={formatMessage(messages.fuelType)}
                            value={mapToPicklistValue({
                                options: this.getFuelTypeOptions(
                                    this.props[picklistService.PICKLIST_FUEL_TYPE]
                                ),
                                selectedGuid: equipment[model.PROPS_FUEL_TYPE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_FUEL_TYPE,
                                        guid: model.PROPS_FUEL_TYPE_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-fuel-type-select"
                        />
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.cylinders)}
                            value={equipment[model.PROPS_CYLINDERS] || ""}
                            precision={4}
                            scale={0}
                            onChange={(value) => this.onNumberChange(model.PROPS_CYLINDERS, value)}
                            testId="equipment-cylinders-input"
                        />
                    </SubSection>
                    <SubSection testId="equipment-engine-displacement-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.engineDisplacement)}
                            value={equipment[model.PROPS_ENGINE_DISPLACEMENT] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_ENGINE_DISPLACEMENT, value)
                            }
                            testId="equipment-engine-displacement-input"
                        />
                    </SubSection>
                </Section>
                <Section
                    headerText={formatMessage(messages.containerInfo)}
                    testId="equipment-container-info-section"
                >
                    <SubSection testId="equipment-container-size-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.containerSize)}
                            value={equipment[model.PROPS_CONTAINER_SIZE] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_CONTAINER_SIZE, value)
                            }
                            testId="equipment-container-size-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getContainerSizeUnitOptions(
                                this.props[unitService.UNIT_VOLUME]
                            )}
                            containerClassNames={getAgBytesErrorClassNames(406, apiErrors)}
                            placeholderText={formatMessage(messages.containerSizeUnit)}
                            value={mapToPicklistValue({
                                options: this.getContainerSizeUnitOptions(
                                    this.props[unitService.UNIT_VOLUME]
                                ),
                                selectedGuid: equipment[model.PROPS_CONTAINER_SIZE_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_CONTAINER_SIZE_UNIT,
                                        guid: model.PROPS_CONTAINER_SIZE_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-container-size-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-physical-state-subsection">
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getPhysicalStateOptions(
                                this.props[picklistService.PICKLIST_PHYSICAL_STATE]
                            )}
                            placeholderText={formatMessage(messages.physicalState)}
                            value={mapToPicklistValue({
                                options: this.getPhysicalStateOptions(
                                    this.props[picklistService.PICKLIST_PHYSICAL_STATE]
                                ),
                                selectedGuid: equipment[model.PROPS_PHYSICAL_STATE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        guid: model.PROPS_PHYSICAL_STATE_GUID,
                                        type: null,
                                    },
                                    value,
                                    this.onPhysicalStateChange(
                                        {
                                            guid: model.PROPS_PHYSICAL_STATE_GUID,
                                        },
                                        value
                                    )
                                );
                            }}
                            testId="equipment-physical-state-select"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    renderDetailInfo2 = () => {
        const { apiErrors } = this.props;
        const { formatMessage } = this.props.intl;
        const { equipment } = this;
        return (
            <div className="section-container section-column">
                <Section
                    headerText={formatMessage(messages.weightInfo)}
                    testId="equipment-weight-info-section"
                >
                    <SubSection testId="equipment-gross-and-tare-weight-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.maxGrossWeight)}
                            value={equipment[model.PROPS_MAX_GROSS_WEIGHT] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_MAX_GROSS_WEIGHT, value)
                            }
                            testId="equipment-max-gross-weight-input"
                        />
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.tareWeight)}
                            value={equipment[model.PROPS_TARE_WEIGHT] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) =>
                                this.onNumberChange(model.PROPS_TARE_WEIGHT, value)
                            }
                            testId="equipment-tare-weight-input"
                        />
                    </SubSection>
                    <SubSection testId="equipment-weight-unit-subsection">
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getWeightUnitOptions(this.props[unitService.UNIT_WEIGHT])}
                            placeholderText={formatMessage(messages.weightUnit)}
                            containerClassNames={getAgBytesErrorClassNames(407, apiErrors)}
                            value={mapToPicklistValue({
                                options: this.getWeightUnitOptions(
                                    this.props[unitService.UNIT_WEIGHT]
                                ),
                                selectedGuid: equipment[model.PROPS_WEIGHT_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_WEIGHT_UNIT,
                                        guid: model.PROPS_WEIGHT_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-weight-unit-select"
                        />
                    </SubSection>
                </Section>
                <Section
                    headerText={formatMessage(messages.sizeInfo)}
                    testId="equipment-size-info-section"
                >
                    <SubSection testId="equipment-width-and-length-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.width)}
                            value={equipment[model.PROPS_WIDTH] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) => this.onNumberChange(model.PROPS_WIDTH, value)}
                            testId="equipment-width-input"
                        />
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.length)}
                            value={equipment[model.PROPS_LENGTH] || ""}
                            precision={9}
                            scale={2}
                            onChange={(value) => this.onNumberChange(model.PROPS_LENGTH, value)}
                            testId="equipment-length-input"
                        />
                    </SubSection>
                    <SubSection testId="equipment-height-and-dimension-unit-subsection">
                        <NumericInput
                            tabIndex={0}
                            placeholderText={formatMessage(messages.height)}
                            value={equipment[model.PROPS_HEIGHT] || ""}
                            precision={8}
                            scale={2}
                            onChange={(value) => this.onNumberChange(model.PROPS_HEIGHT, value)}
                            testId="equipment-height-input"
                        />
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getDimensionUnitOptions(
                                this.props[unitService.UNIT_LENGTH]
                            )}
                            placeholderText={formatMessage(messages.dimensionUnit)}
                            containerClassNames={getAgBytesErrorClassNames(407, apiErrors)}
                            value={mapToPicklistValue({
                                options: this.getDimensionUnitOptions(
                                    this.props[unitService.UNIT_LENGTH]
                                ),
                                selectedGuid: equipment[model.PROPS_DIMENSION_UNIT_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_DIMENSION_UNIT,
                                        guid: model.PROPS_DIMENSION_UNIT_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-dimension-unit-select"
                        />
                    </SubSection>
                    <SubSection testId="equipment-class-size-subsection">
                        <SelectInput
                            tabIndex={0}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getClassSizeOptions(
                                this.props[picklistService.PICKLIST_CLASS_SIZE]
                            )}
                            placeholderText={formatMessage(messages.classSize)}
                            value={mapToPicklistValue({
                                options: this.getClassSizeOptions(
                                    this.props[picklistService.PICKLIST_CLASS_SIZE]
                                ),
                                selectedGuid: equipment[model.PROPS_CLASS_SIZE_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_CLASS_SIZE,
                                        guid: model.PROPS_CLASS_SIZE_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="equipment-class-size-select"
                        />
                    </SubSection>
                    <Section
                        headerText={formatMessage(messages.towingInfo)}
                        testId="equipment-towing-info-section"
                    >
                        <SubSection testId="equipment-towing-capacity-subsection">
                            <NumericInput
                                tabIndex={0}
                                placeholderText={formatMessage(messages.towingCapacity)}
                                value={equipment[model.PROPS_TOWING_CAPACITY] || ""}
                                precision={9}
                                scale={2}
                                onChange={(value) =>
                                    this.onNumberChange(model.PROPS_TOWING_CAPACITY, value)
                                }
                                testId="equipment-towing-capacity-input"
                            />
                            <SelectInput
                                tabIndex={0}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                options={this.getTowingCapacityUnitOptions(
                                    this.props[unitService.UNIT_WEIGHT]
                                )}
                                placeholderText={formatMessage(messages.towingCapacityUnit)}
                                containerClassNames={getAgBytesErrorClassNames(409, apiErrors)}
                                value={mapToPicklistValue({
                                    options: this.getTowingCapacityUnitOptions(
                                        this.props[unitService.UNIT_WEIGHT]
                                    ),
                                    selectedGuid: equipment[model.PROPS_TOWING_CAPACITY_UNIT_GUID],
                                })}
                                onChange={(value) => {
                                    this.onPicklistChange(
                                        {
                                            type: model.PROPS_TOWING_CAPACITY_UNIT,
                                            guid: model.PROPS_TOWING_CAPACITY_UNIT_GUID,
                                        },
                                        value
                                    );
                                }}
                                testId="equipment-towing-capacity-unit-select"
                            />
                        </SubSection>
                    </Section>
                </Section>
            </div>
        );
    };

    renderDetailInfo3 = () => {
        const { formatMessage } = this.props.intl;
        const { equipment } = this;
        return (
            <div className="section-container section-column">
                <Section
                    headerText={formatMessage(messages.miscellaneousInfo)}
                    testId="equipment-miscellaneous-info-section"
                >
                    <SubSection testId="equipment-ag-gateway-id-subsection">
                        <TextInput
                            maxLength={50}
                            tabIndex={0}
                            placeholderText={formatMessage(messages.agGatewayID)}
                            value={equipment[model.PROPS_AGGATEWAY_ID]}
                            onChange={(value) => this.onTextChange(model.PROPS_AGGATEWAY_ID, value)}
                            testId="equipment-ag-gateway-id-input"
                        />
                    </SubSection>
                    <SubSection
                        className="form-section-child-stretch"
                        testId="equipment-description-subsection"
                    >
                        <TextArea
                            placeholderText={formatMessage(messages.description)}
                            value={equipment[model.PROPS_DESCRIPTION]}
                            onChange={(value) => this.onTextChange(model.PROPS_DESCRIPTION, value)}
                            testId="equipment-description-textarea"
                        />
                    </SubSection>
                </Section>
                <Section
                    className="grid-section"
                    headerText={formatMessage(messages.equipmentPhotos)}
                    testId="equipment-equipment-photos-section"
                >
                    <SubSection testId="equipment-equipment-photos-subsection">
                        <PhotoList
                            formatMessage={formatMessage}
                            record={equipment[model.PROPS_EQUIPMENT_PHOTOLIST]}
                            itemListAlias={model.PROPS_EQUIPMENT_PHOTOLIST}
                            onTextChange={this.onTextChange}
                        />
                    </SubSection>
                </Section>
                <Section
                    className="grid-section"
                    headerText={formatMessage(messages.equipmentTypeAlias)}
                    testId="equipment-equipment-type-alias-section"
                >
                    <SubSection testId="equipment-equipment-type-alias-subsection">
                        <AliasList
                            formatMessage={formatMessage}
                            aliasTextLabel="equipmentTypeAlias"
                            record={equipment[model.PROPS_EQUIPMENT_TYPE_ALIASLIST]}
                            itemListAlias={model.PROPS_EQUIPMENT_TYPE_ALIASLIST}
                            onTextChange={this.onTextChange}
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    render() {
        return (
            <div className="add-edit-panel">
                {this.renderEquipmentInfo()}
                <div className="section-container">
                    {this.renderDetailInfo1()}
                    <span className="bar section-fence"></span>
                    {this.renderDetailInfo2()}
                    <span className="bar section-fence"></span>
                    {this.renderDetailInfo3()}
                </div>
            </div>
        );
    }
}

export default injectIntl(AddEditPanel);
